/* eslint-disable max-lines */
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';

import useInfoDialog from 'common/hooks/useInfoDialog';
import { refundTransaction } from 'store/thunks/transactionsThunk';

import TransactionAmountSelector from './TransactionAmountSelector';

const RefundTransactionButton = ({
  amount,
  patientId,
  externalTransactionId,
  userId,
  transactionId,
}) => {
  const dispatch = useDispatch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initialValues = { amountType: 'fullAmount' };

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const handleConfirmAmount = (form) => {
    const isOtherAmount = form.amountType === 'otherAmount';
    const { otherAmount } = form;
    const isLessOrEqualTotalAmount = Number(otherAmount) <= Number(amount);

    const amountToRefund =
      isOtherAmount && isLessOrEqualTotalAmount // Don't refund more than the total amount
        ? Number(otherAmount)
        : Number(amount);

    showInfoDialog({
      title: 'Confirm Refund',
      message: 'Are you sure you want to refund this amount?',
      callback: () => {
        setSubmitting(true);

        dispatch(
          refundTransaction({
            amount: amountToRefund,
            patientId,
            externalTransactionId,
            userId,
            transactionId,
          })
        ).then(() => {
          setSubmitting(false);
        });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Refund',
    });
  };

  const handleRefundTransaction = () => setShowModal(true);

  return (
    <>
      <InfoDialog />

      <Button
        sx={{ width: 75 }}
        variant='outlined'
        color='secondary'
        size='medium'
        onClick={handleRefundTransaction}
      >
        Refund
      </Button>

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='Transactions-select-amount-header'
        fullWidth
      >
        <DialogTitle id='Transactions-select-amount-header'>Confirm Amount</DialogTitle>

        <Form
          initialValues={initialValues}
          onSubmit={handleConfirmAmount}
          render={({ handleSubmit, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <TransactionAmountSelector
                  amount={amount}
                  label='How much do you want to refund?'
                />
              </DialogContent>

              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  loading={submitting}
                  disabled={invalid}
                  type='submit'
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

RefundTransactionButton.propTypes = {
  amount: PropTypes.number.isRequired,
  patientId: PropTypes.string.isRequired,
  externalTransactionId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  transactionId: PropTypes.string.isRequired,
};

export default RefundTransactionButton;
